import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Tab, Tabs } from "react-bootstrap";
import { isFulfilled } from "@reduxjs/toolkit";
import { ADMIN_LANG, AVAILABLE_LANGUAGES } from "../../../settings";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import {
    changeCollectionModal,
    closeModal,
    deleteCollection,
    getCollection,
    saveCollection,
    saveNewCollection,
    setActiveLanguage,
    uploadCollectionLiftImage,
} from "../../../app/slices/modals/collectionEditModal";
import AdminLoader from "../AdminLoader";
import AsyncTeamsSelect from "../gameView/AsyncTeamsSelect";
import AsyncGamesSelect from "../gameView/AsyncGamesSelect";
import { CollectionLanguage, PlainDetails } from "../../../types/admin";
import RichTextField from "../RichTextField";
import ImageField from "../ImageField";
import { v4 as uuidv4 } from "uuid";
import CollectionSlideEditor from "./collectionEditModal/CollectionSlideEditor";
import CollectionElementModal from "./collectionEditModal/CollectionElementModal";
import { useNavigate } from "react-router-dom";
import Confirm from "../Confirm";
import { getSelectOptions } from "../../../app/slices/admin";

const CollectionEditModal = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {
        isOpen,
        collection_id,
        error,
        languages,
        loading,
        languageTab,
        teams,
        team_options,
        games,
        slide_id,
        lift_image,
        isUploading,
    } = useAppSelector((state) => state.collectionEditModal);

    const [pendingLiftImage, setPendingLiftImage] = useState<File | null>(null);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);

    useEffect(() => {
        if (isOpen && collection_id) {
            dispatch(getCollection({ collection_id }));
        } else if (isOpen && !collection_id) {
            dispatch(getSelectOptions(['team_options']));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, collection_id]);

    const handleTabSelect = (key: string | null) => {
        if (key) {
            dispatch(setActiveLanguage(key));
        }
    };

    const handleClose = () => {
        dispatch(closeModal());
        setPendingLiftImage(null);
    };

    const handleDelete = () => {
        setShowConfirmDelete(true);
    };

    const handleConfirmDelete = () => {
        setShowConfirmDelete(false);
        if (!collection_id) {
            return;
        }
        dispatch(deleteCollection(collection_id)).then((resultAction) => {
            if (isFulfilled(resultAction)) {
                navigate('/admin/collections');
            }
        });
    };

    const updateLanguageField = useCallback((updater: (language: CollectionLanguage) => CollectionLanguage) => {
        const updated = languages.map((language) =>
            language.language_code === languageTab ? updater(language) : language
        );
        if (!updated.some(lang => lang.language_code === languageTab)) {
            updated.push(updater({ language_code: languageTab, language_name: '', name: '', slug: '', lift_text: '', is_active: true }));
        }
        dispatch(changeCollectionModal({ languages: updated }));
    }, [dispatch, languageTab, languages]);

    const handleNameChange = (name: string) => {
        updateLanguageField((language) => ({ ...language, name }));
    };

    const handleSlugChange = (slug: string) => {
        updateLanguageField((language) => ({ ...language, slug }));
    };

    const handleLiftTextChange = (lift_text: string) => {
        updateLanguageField((language) => ({ ...language, lift_text }));
    };

    const handleIsActiveChange = (is_active: boolean) => {
        updateLanguageField((language) => ({ ...language, is_active }));
    };

    const handleTeamsChange = (selected: PlainDetails[]) => {
        dispatch(changeCollectionModal({ teams: selected }));
    };

    const handleGamesChange = (selected: PlainDetails[]) => {
        dispatch(changeCollectionModal({ games: selected }));
    };

    const handleLiftImageChange = (image: File | null) => {
        if (!collection_id) {
            setPendingLiftImage(image);
            return;
        }

        if (image) {
            const formData = new FormData();
            formData.append('collection_id', collection_id);
            formData.append('file', image, uuidv4() + image.name);
            dispatch(uploadCollectionLiftImage(formData));
        } else {
            dispatch(changeCollectionModal({ lift_image: null }));
        }
    };

    const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const modalForm = e.currentTarget;

        if (!modalForm.checkValidity()) {
            e.stopPropagation();
            return;
        }

        if (collection_id) {
            const payload = {
                collection_id,
                languages,
                teams,
                games
            };
            dispatch(saveCollection(payload));
        } else {
            const payload = {
                languages,
                teams,
                games
            };
            dispatch(saveNewCollection(payload)).then((resultAction) => {
                if (saveNewCollection.fulfilled.match(resultAction)) {
                    if (pendingLiftImage && resultAction.payload.collection_id) {
                        const formData = new FormData();
                        formData.append('collection_id', resultAction.payload.collection_id);
                        formData.append('file', pendingLiftImage, uuidv4() + pendingLiftImage.name);
                        dispatch(uploadCollectionLiftImage(formData));
                        setPendingLiftImage(null);
                    }
                    navigate(`/admin/collections/?new=${resultAction.payload.collection_id}`);
                }
            });
        }
    };

    const collectionTitle = collection_id
        ? (languages.find(lang => lang.language_code === languageTab)?.name || ADMIN_LANG.collection)
        : ADMIN_LANG.new_collection;

    const nameHelpText = languages
        .filter(lang => lang.language_code !== languageTab && lang.name)
        .map(lang => (
            <div key={lang.language_code}>
                <strong>{lang.language_code.toUpperCase()}: </strong>
                {lang.name}
            </div>
        ));

    return (
        <Modal size='xl' show={isOpen} onHide={handleClose} className='admin-modal'>
            {loading && <AdminLoader />}
            <Modal.Header closeButton>
                <Modal.Title>{collectionTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant='danger' dismissible>
                        <div dangerouslySetInnerHTML={{ __html: error }} />
                    </Alert>
                )}
                <Form id='collection-modal-form' onSubmit={handleSave}>
                    <Row className='gy-3'>
                        <Col xs={12}>
                            <Tabs className='mb-3' activeKey={languageTab} onSelect={handleTabSelect}>
                                {AVAILABLE_LANGUAGES.map(language => (
                                    <Tab eventKey={language} title={language.toUpperCase()} key={language}>
                                        <Row className='gy-3 pt-3'>
                                            <Col xs={12}>
                                                <Form.Group controlId={`collection_name_${language}`}>
                                                    <Form.Label>{`${ADMIN_LANG.name} (${language.toUpperCase()})`}</Form.Label>
                                                    <Form.Control
                                                        type='text'
                                                        maxLength={255}
                                                        onChange={(e) => handleNameChange(e.target.value)}
                                                        value={languages.find((lang) => lang.language_code === language)?.name || ''}
                                                        required={language === languageTab}
                                                    />
                                                    {nameHelpText.length > 0 &&
                                                        <Form.Text className='help-text'>
                                                            {nameHelpText}
                                                        </Form.Text>
                                                    }
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12}>
                                                <Form.Group controlId={`collection_slug_${language}`}>
                                                    <Form.Label>{`${ADMIN_LANG.slug} (${language.toUpperCase()})`}</Form.Label>
                                                    <Form.Control
                                                        type='text'
                                                        maxLength={255}
                                                        onChange={(e) => handleSlugChange(e.target.value)}
                                                        value={languages.find((lang) => lang.language_code === language)?.slug || ''}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12}>
                                                <Form.Group controlId={`collection_lift_text_${language}`}>
                                                    <Form.Label>{`${ADMIN_LANG.lift_text} (${language.toUpperCase()})`}</Form.Label>
                                                    <RichTextField
                                                        value={languages.find((lang) => lang.language_code === language)?.lift_text || ''}
                                                        onChange={handleLiftTextChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12}>
                                                <Form.Group controlId={`collection_is_active_${language}`}>
                                                    <Form.Switch
                                                        checked={languages.find((lang) => lang.language_code === language)?.is_active || false}
                                                        onChange={(e) => handleIsActiveChange(e.target.checked)}
                                                        className='form-label'
                                                        label={`${ADMIN_LANG.is_active} (${language.toUpperCase()})`}
                                                    />
                                                    <Form.Text className='help-text'>
                                                        Näytetäänkö kokoelma tällä kielellä julkisessa näkymässä.
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Tab>
                                ))}
                            </Tabs>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Group controlId={`collection_teams`}>
                                <Form.Label>{ADMIN_LANG.allowed_teams}</Form.Label>
                                <AsyncTeamsSelect selectedTeams={teams} onChange={handleTeamsChange} initialValues={team_options} />
                                <Form.Text className='help-text'>{ADMIN_LANG.team_help}</Form.Text>
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Group controlId={`collection_games`}>
                                <Form.Label>{ADMIN_LANG.collection_games}</Form.Label>
                                <AsyncGamesSelect selectedGames={games} onChange={handleGamesChange} initialValues={games} />
                            </Form.Group>
                        </Col>
                        <Col xs={12}>
                            <Form.Group className='d-flex flex-column' controlId={`collection_lift_image`}>
                                <ImageField
                                    defaultImage={lift_image}
                                    setImage={handleLiftImageChange}
                                    label={ADMIN_LANG.lift_image}
                                    imageWidth={'300px'}
                                />
                            </Form.Group>
                        </Col>
                        {slide_id && (
                            <Col xs={12}>
                                <CollectionSlideEditor />
                            </Col>
                        )}
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {collection_id && (
                    <Button variant='danger' className='me-auto' onClick={handleDelete}>
                        {ADMIN_LANG.delete}
                    </Button>
                )}
                <Button variant='outline-primary' onClick={handleClose}>
                    {ADMIN_LANG.close}
                </Button>
                <Button variant='primary' form='collection-modal-form' type='submit' disabled={isUploading}>
                    {ADMIN_LANG.save}
                </Button>
            </Modal.Footer>
            <CollectionElementModal />
            <Confirm
                show={showConfirmDelete}
                message={ADMIN_LANG.confirm_delete_collection}
                onConfirm={handleConfirmDelete}
                onCancel={() => setShowConfirmDelete(false)}
            />
        </Modal>
    );
};

export default CollectionEditModal;
